import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getOrder, resolveOrderShippingAddress, formatOrderNumber, type Order, type ShippingAddress } from '../../../utils/firebase';
import { ArrowLeft, Package, Mail, MapPin, CreditCard } from 'lucide-react';
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}
export function AdminOrderDetail() {
  const {
    orderId
  } = useParams<{
    orderId: string;
  }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [legacyShippingAddress, setLegacyShippingAddress] = useState<ShippingAddress | undefined>();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!orderId) return;
    let active = true;
    getOrder(orderId).then(async data => {
      if (!active) return;
      setOrder(data);
      setLegacyShippingAddress(undefined);
      const needsLegacyAddress = data && !data.shippingAddress?.line1 && data.lineItems.some(item => item.type === 'supplement');
      if (!needsLegacyAddress || !data) return;
      const resolvedAddress = await resolveOrderShippingAddress(data);
      if (active) setLegacyShippingAddress(resolvedAddress);
    }).catch(error => {
      console.error('[Admin] Could not load order details:', error);
      if (active) setOrder(null);
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [orderId]);
  if (loading) {
    return <div className="p-8 flex justify-center" data-id="element-339" data-mp="43tfcwj2vwi4">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-340" data-mp="42ieb4h0p2up" />
      </div>;
  }
  if (!order) {
    return <div className="p-8 text-center" data-id="element-341" data-mp="1tl8hu8l7p0nv">
        <p className="text-[#5A5A5A]" data-id="element-342" data-mp="1tk6xfdib31th">Order not found.</p>
        <Link to="/admin/orders" className="text-tealslate hover:underline mt-4 inline-block" data-id="element-343" data-mp="1ybf6u9jmyn6n">
          Back to Orders
        </Link>
      </div>;
  }
  const hasPhysical = order.lineItems.some(i => i.type === 'supplement');
  const hasDigital = order.lineItems.some(i => i.type === 'book');
  const shippingAddress = order.shippingAddress?.line1 ? order.shippingAddress : legacyShippingAddress;
  return <div className="p-8 max-w-4xl mx-auto" data-id="element-344" data-mp="2ha18bpj2egb3">
      <Link to="/admin/orders" className="inline-flex items-center gap-2 text-sm font-medium text-[#5A5A5A] hover:text-[#111111] mb-6 transition-colors" data-id="element-345" data-mp="22diurxnhrk8t">
        <ArrowLeft className="w-4 h-4" data-id="element-346" data-mp="27eg7vvtn9o3g" />
        Back to Orders
      </Link>

      <div className="flex items-center justify-between mb-8" data-id="element-347" data-mp="qwk86cnql4z9">
        <div data-id="element-348" data-mp="vxgjvcebxvf8">
          <h1 className="text-2xl font-bold text-[#111111] mb-1" data-id="element-349" data-mp="vy9ly02cfwr1">
            Order #{formatOrderNumber(order.id)}
          </h1>
          <p className="text-sm text-[#5A5A5A]" data-id="element-350" data-mp="3a34sgz6wcirf">
            {formatDate(order.createdAt)}
          </p>
        </div>
        <div className="flex gap-2" data-id="element-351" data-mp="3588znwtu5rd9">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider
            ${order.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-352" data-mp="3j5xiz0898qg7">
            {order.status}
          </span>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider
            ${order.fulfillmentStatus === 'pending' ? 'bg-amber-100 text-amber-800' : order.fulfillmentStatus === 'shipped' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-353" data-mp="2mfwtez02a5xu">
            {order.fulfillmentStatus.replace('_', ' ')}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-354" data-mp="23o3zn2d64gp2">
        {/* Customer Info */}
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 shadow-sm" data-id="element-355" data-mp="2gffvy1o4zdwd">
          <div className="flex items-center gap-2 mb-4 text-[#111111]" data-id="element-356" data-mp="2le96bl84mosb">
            <Mail className="w-5 h-5 text-[#9A9A90]" data-id="element-357" data-mp="2qckelrirwr8f" />
            <h2 className="font-bold" data-id="element-358" data-mp="2qazoce3hrugd">Customer</h2>
          </div>
          <p className="text-sm font-medium text-[#111111] mb-1" data-id="element-359" data-mp="2v42ioog3f7xm">
            {shippingAddress?.recipientName || 'Digital Customer'}
          </p>
          <p className="text-sm text-[#5A5A5A] break-all" data-id="element-360" data-mp="3046sjvmhx4ev">{order.email}</p>
        </div>

        {/* Shipping Info */}
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 shadow-sm" data-id="element-361" data-mp="392sdfxffwvit">
          <div className="flex items-center gap-2 mb-4 text-[#111111]" data-id="element-362" data-mp="390z8qnoe0ldg">
            <MapPin className="w-5 h-5 text-[#9A9A90]" data-id="element-363" data-mp="3e1d7tg80gkzc" />
            <h2 className="font-bold" data-id="element-364" data-mp="1epan62nnhkk3">Shipping</h2>
          </div>
          {!hasPhysical ? <p className="text-sm text-[#5A5A5A] italic" data-id="element-365" data-mp="1jnc8yvcgnxz9">
              Digital Product - No shipping required.
            </p> : shippingAddress ? <div className="text-sm text-[#5A5A5A] space-y-1" data-id="element-366" data-mp="1c6fpq2jv9fyc">
              <p className="font-medium text-[#111111]" data-id="element-367" data-mp="17cuom1mz85ux">
                {shippingAddress.recipientName}
              </p>
              <p data-id="element-368" data-mp="12et4rehq82ie">{shippingAddress.line1}</p>
              {shippingAddress.line2 && <p data-id="element-369" data-mp="xghvb4vcfu0p">{shippingAddress.line2}</p>}
              <p data-id="element-370" data-mp="xdlw7d7rezao">
                {shippingAddress.city}, {shippingAddress.state}{' '}
                {shippingAddress.postalCode}
              </p>
              <p data-id="element-371" data-mp="1q55nzlif9loa">{shippingAddress.country}</p>
              {shippingAddress.deliveryInstructions?.trim() && <div className="mt-3 pt-3 border-t border-[#E5E5E5]" data-id="element-372" data-mp="1l3q3pvo2jj6y">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#9A9A90] mb-1" data-id="element-373" data-mp="1g6pu5as0fyb3">
                    Delivery instructions
                  </p>
                  <p className="whitespace-pre-line" data-id="element-374" data-mp="9nhwhgxozjoy">
                    {shippingAddress.deliveryInstructions}
                  </p>
                </div>}
              {order.trackingNumber && <p className="mt-3 pt-3 border-t border-[#E5E5E5]" data-id="element-375" data-mp="2wy1ru6jd5433">
                  <span className="font-medium text-[#111111]" data-id="element-376" data-mp="2ww7h1l1t2scq">Tracking:</span>{' '}
                  {order.trackingNumber}
                </p>}
            </div> : <p className="text-sm text-[#5A5A5A]" data-id="element-377" data-mp="2nvsj9m7nnksc">
              No shipping address provided.
            </p>}
        </div>

        {/* Payment Info */}
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 shadow-sm" data-id="element-378" data-mp="2e0hdvui4zpx9">
          <div className="flex items-center gap-2 mb-4 text-[#111111]" data-id="element-379" data-mp="208kmxts7fm2g">
            <CreditCard className="w-5 h-5 text-[#9A9A90]" data-id="element-380" data-mp="2575e73phaver" />
            <h2 className="font-bold" data-id="element-381" data-mp="257nimnmaofxr">Payment</h2>
          </div>
          <p className="text-sm text-[#5A5A5A] mb-1" data-id="element-382" data-mp="1q9d3couvmv6w">
            Status:{' '}
            <span className="font-medium text-[#111111] capitalize" data-id="element-383" data-mp="1v8y8jh0n3zs2">
              {order.status}
            </span>
          </p>
          <p className="text-sm text-[#5A5A5A] break-all" data-id="element-384" data-mp="1bo7jqcidcr1r">
            Session: {order.stripeSessionId}
          </p>
        </div>
      </div>

      {/* Line Items */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl shadow-sm overflow-hidden mb-8" data-id="element-385" data-mp="1llc54a4e4ihi">
        <div className="p-4 border-b border-[#E5E5E5] bg-[#FAFAFA]" data-id="element-386" data-mp="1gmitkn8nz1jz">
          <h2 className="font-bold text-[#111111]" data-id="element-387" data-mp="1goc2k96r1xv5">Products Ordered</h2>
        </div>
        <table className="w-full text-left border-collapse" data-id="element-388" data-mp="1ukh6rpb90rxw">
          <thead data-id="element-389" data-mp="1ql8delk4ew2v">
            <tr className="border-b border-[#E5E5E5]" data-id="element-390" data-mp="1qjnigutw9b4h">
              <th className="px-6 py-3 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-391" data-mp="24btvw95nx408">
                Item
              </th>
              <th className="px-6 py-3 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider text-right" data-id="element-392" data-mp="1zds5121zw0e7">
                Price
              </th>
              <th className="px-6 py-3 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider text-right" data-id="element-393" data-mp="2e7nb60arnblp">
                Qty
              </th>
              <th className="px-6 py-3 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider text-right" data-id="element-394" data-mp="txkhsshd6y8r">
                Total
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E5]" data-id="element-395" data-mp="13widcfd7js19">
            {order.lineItems.map((item, i) => <tr key={i} data-id="element-396" data-mp="18utkujg15qgb" data-mpi={i}>
                <td className="px-6 py-4" data-id="element-397" data-mp="18v3dkesgmbpp">
                  <p className="text-sm font-medium text-[#111111]" data-id="element-398" data-mp="b8cr1o6i9uzb">
                    {item.name}
                  </p>
                  <p className="text-xs text-[#5A5A5A] capitalize" data-id="element-399" data-mp="g88v1nf7dmev">
                    {item.type}
                  </p>
                </td>
                <td className="px-6 py-4 text-sm text-[#5A5A5A] text-right" data-id="element-400" data-mp="l47css61066b">
                  {formatPrice(item.unitPrice)}
                </td>
                <td className="px-6 py-4 text-sm text-[#5A5A5A] text-right" data-id="element-401" data-mp="3onskebcr6kon">
                  {item.quantity}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-[#111111] text-right" data-id="element-402" data-mp="3tkiy4s13546w">
                  {formatPrice(item.unitPrice * item.quantity)}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

{/* Order Summary */}
<div className="bg-white border border-[#E5E5E5] rounded-xl shadow-sm p-6 ml-auto w-full md:w-80" data-id="element-403" data-mp="21phyrgicr7tg">
  <h2 className="font-bold text-[#111111] mb-4" data-id="element-404" data-mp="1wrzmph91gch8">Order Summary</h2>

  <div className="space-y-3 text-sm" data-id="element-405" data-mp="1wtkkrgukadju">
    <div className="flex justify-between text-[#5A5A5A]" data-id="element-406" data-mp="1rsdddiwbzgdp">
      <span data-id="element-407" data-mp="1rty22rvys53n">Subtotal</span>
      <span data-id="element-408" data-mp="1n0v84in9ytmx">{formatPrice(order.subtotal)}</span>
    </div>

    {hasPhysical && <div className="flex justify-between text-[#5A5A5A]" data-id="element-409" data-mp="1izjrv3qajkre">
        <span data-id="element-410" data-mp="1j01ldslkjkvm">Shipping</span>
        <span data-id="element-411" data-mp="1e0ozpb6fojum">{formatPrice(order.shipping ?? 0)}</span>
      </div>}

    <div className="flex justify-between text-[#5A5A5A]" data-id="element-412" data-mp="1e2jjgm5yy5x4">
      <span data-id="element-413" data-mp="191vlthn8iqtx">Tax</span>
      <span data-id="element-414" data-mp="190as1u8qvbx4">{formatPrice(order.tax ?? 0)}</span>
    </div>

    {(order.discount ?? 0) > 0 && <div className="flex justify-between text-[#5A5A5A]" data-id="element-415" data-mp="143tzmnuiibe8">
        <span data-id="element-416" data-mp="3singy3bu4ctz">Discount</span>
        <span data-id="element-417" data-mp="3shvkyrebsd36">-{formatPrice(order.discount)}</span>
      </div>}

 <div className="pt-3 border-t border-[#E5E5E5] flex justify-between font-bold text-[#111111] text-base" data-id="element-418" data-mp="3nk2d7txdjb0s">
      <span data-id="element-419" data-mp="56cmqqludan7">Grand Total</span>
      <span data-id="element-420" data-mp="54jhnfqvmyhb">{formatPrice(order.total)}</span>
    </div>
  </div>
</div>

    </div>;
}